import Vue, { CreateElement } from '@src/vue'
import { Component } from 'vue-property-decorator'

import ApiClient, { ApiCallError, ERROR_MAP } from '@src/lib/api_client'
import { Button } from '@src/elements/button/button'
import { Body1, Subtitle1 } from '@src/elements/typography/typography'
import { API } from '@src/lib/api_path'
import { NotificationTypes } from '@src/util/constants'
import { ConfirmModal } from '@src/elements/modal/confirm-modal'
import { Input } from '@src/elements/input/input'
import { validatePassword } from '@src/util/validation'
import { highlightFulfilledRequirements } from '@src/util/functions'
import { ContentBox } from '@src/elements/content-box/content-box'
import { InputFootnote } from '@src/elements/input/input-footnote'
import { Spacer } from '@src/elements/spacer/spacer'
import { PasswordRequirements } from '@src/components/password-requirements/password-requirements'

@Component({
  computed: {
    email () {
      return this.$store.state.selectedMember.email
    },
    memberId () {
      return this.$store.state.selectedMember.id
    }
  }
})
export default class ChangePassword extends Vue {

  protected email: string
  protected apiClient: ApiClient = new ApiClient()
  protected missingEmailError: boolean = false
  protected showConfirmPopup: boolean = false

  protected memberId: number
  protected newPassword: string = ''
  protected confirmPassword: string = ''
  protected isValidPassword: boolean = true
  protected isValidConfirmPassword: boolean = true

  private successMessage: string = ''

  private highlightRequirements = {
    minLength: false,
    oneLetter: false,
    oneNumber: false,
    sameAsConfirm: false
  }

  constructor () {
    super()
  }

  resetPassword () {
    this.missingEmailError = false

    return this.apiClient.post(API.TRAINERS_API.REQUEST_RESET_PASSWORD, { email: this.email })
      .then(() => this.$store.dispatch('showToast', { message: this.$t('PAGE.MEMBERS.CHANGE_PASSWORD.RESET_EMAIL_SENT'), fadeOut: true }))
      .catch(() => this.missingEmailError = true)

  }

  showNotification (message: string) {
    this.successMessage = message
    this.showConfirmPopup = true
  }

  hideNotification () {
    this.successMessage = ''
    this.showConfirmPopup = false
  }

  setPassword () {
    this.validate()

    if (this.isValidPassword && this.isValidConfirmPassword) {
      this.submitNewPassword()
    }
    this.scrollError()
  }

  submitNewPassword () {
    this.apiClient.put(API.TRAINERS_API.MEMBER_DETAILS.replace('{memberId}', `${this.memberId}`), { member: { password: this.newPassword } })
      .then(() => {
        this.$store.dispatch('showToast', { message: this.$t('PAGE.MEMBERS.CHANGE_PASSWORD.CHANGE_PASSWORD_DONE'), fadeOut: true })
        this.newPassword = ''
        this.confirmPassword = ''
      })
      .catch((error: ApiCallError) => {
        if (error.messageKey === ERROR_MAP['422']) {
          this.isValidPassword = false
        }
      })
  }

  validate () {
    this.isValidPassword = validatePassword(this.newPassword)
    this.isValidConfirmPassword = this.newPassword === this.confirmPassword
    if (!this.isValidPassword || !this.isValidConfirmPassword) {
      this.scrollError()
    }
  }

  scrollError () {
    let container = document.querySelector('.scroll-content')
    if (container) {
      let firstErrorInput = container.querySelector('.change-password')
      if (firstErrorInput) {
        let containerOffsetTop = container.getBoundingClientRect().top
        let errorOffsetTop = firstErrorInput.getBoundingClientRect().top
        container.scrollTop += errorOffsetTop - containerOffsetTop
      }
    }
  }

  setNewPassword (value: string) {
    this.newPassword = value

    this.highlightRequirements = highlightFulfilledRequirements(this.newPassword, this.confirmPassword)
  }

  setConfirmPassword (value: string) {
    this.confirmPassword = value

    this.highlightRequirements = highlightFulfilledRequirements(this.newPassword, this.confirmPassword)
  }

  render (h: CreateElement) {

    return <div class='change-password' direction='column'>
      <Subtitle1>{this.$t('PAGE.MEMBERS.CHANGE_PASSWORD.CHANGE_PASSWORD')}</Subtitle1>

      <ContentBox>
        <div slot='content'>
          {this.renderChangePasswordForm(h)}

          <PasswordRequirements highlightRequirements={this.highlightRequirements} />

          {this.email ?
            <div>
              <Spacer />
              <Subtitle1>{this.$t('PAGE.MEMBERS.CHANGE_PASSWORD.RESET_MEMBER_PASSWORD')}</Subtitle1>
              <Body1> {this.$t('PAGE.MEMBERS.CHANGE_PASSWORD.RESET_MEMBER_PASSWORD_INFO')}</Body1>
            </div>
            : null}
        </div>

        <div class='btn-row' slot='bottom'>
          {this.email ? <Button class='save-btn' label={this.$t('RESET')} click={this.resetPassword} ref='resetPassword' type='outline' /> : null}
          <Button class='save-btn' label={this.$t('PAGE.MEMBERS.CHANGE_PASSWORD.CHANGE_PASSWORD')} click={this.setPassword} ref='setPassword' type='elevated' />
        </div>
      </ContentBox>

      <ConfirmModal show={this.showConfirmPopup}
                    closeCallback={this.hideNotification}
                    message={this.$t(this.successMessage)}
                    type={NotificationTypes.NOTIFY} />
    </div>
  }

  renderChangePasswordForm (h: CreateElement) {
    return <div class='row'>
      <div class='short'>
        <Input ref='newPassword' type='password' class={!this.isValidPassword ? 'error' : ''} value={this.newPassword} valueChanged={this.setNewPassword}
               label={`${this.$t('PAGE.MEMBERS.CHANGE_PASSWORD.NEW')}*`} />
        {this.missingEmailError ? <InputFootnote class='error'>
          <span class='error'>{this.$t('PAGE.MEMBERS.CHANGE_PASSWORD.CHANGE_PASSWORD_MISSING_EMAIL')}</span>
        </InputFootnote> : null}
      </div>
      <Input ref='confirmPassword' type='password' class={`short ${!this.isValidConfirmPassword ? 'error' : ''}`} value={this.confirmPassword}
             valueChanged={this.setConfirmPassword} label={`${this.$t('PAGE.MEMBERS.CHANGE_PASSWORD.CONFIRM')}*`} />
    </div>
  }
}
